import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { message, Spin } from "antd";
import { HeaderComponent, BodyComponent } from "@comps/components";
import { STEPS } from "./constants";
import { resolvePresets } from "./presets";
import {
  buildStepStatuses,
  createEmptyReadiness,
  getBlockingSetupItems,
  getOnboardingScopeKey,
  loadOnboardingReadiness,
  SETUP_STATUS,
} from "./readiness";
import { StatusTag } from "./components/ReadinessSummary";
import Step1Org from "./components/Step1Org";
import Step3Employees from "./components/Step3Employees";
import StepShiftAssignment from "./components/StepShiftAssignment";
import StepAttendanceMachines from "./components/StepAttendanceMachines";
import StepLeaveAssignments from "./components/StepLeaveAssignments";
import StepLoanSettings from "./components/StepLoanSettings";
import StepRequestFlow from "./components/StepRequestFlow";
import StepPayrollPolicies from "./components/StepPayrollPolicies";
import Step6GoLive from "./components/Step6GoLive";
import "./onboarding.css";

// ---------------------------------------------------------------------------
// Container / orchestrator for the self-onboarding wizard.
//
// Step contract — every StepN receives:
//   wizard   : accumulated wizard state (read for defaults/summary)
//   presets  : resolved industry+country preset bundle (from presets.js)
//   patch(p) : shallow-merge p into wizard state + persist draft
//   onNext() : advance to the next step
//   onBack() : go to the previous step
//   goLive() : (step 6 only) finish + leave the wizard
//
// Commit model = progressive: each step persists its own records through the
// existing per-module requests, then patch()es any ids/counts it produced and
// calls onNext(). Nothing here owns a "commit-all" endpoint.
// ---------------------------------------------------------------------------

const visualStepStatus = (index, current) => {
  if (index < current) return "done";
  if (index === current) return "current";
  return "todo";
};

const readDraft = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

const OnboardingWizard = () => {
  const navigate = useNavigate();
  const initialDraftKey = useMemo(getOnboardingScopeKey, []);
  const [draftKey, setDraftKey] = useState(initialDraftKey);
  const draft = useMemo(() => readDraft(initialDraftKey), [initialDraftKey]);
  const [current, setCurrent] = useState(draft.__step || 0);
  const [wizard, setWizard] = useState(draft);
  const [readiness, setReadiness] = useState(createEmptyReadiness);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [initialReadinessLoading, setInitialReadinessLoading] = useState(true);

  const persist = useCallback((next, key = draftKey) => {
    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch (e) {
      // best-effort draft only; never block the wizard on storage failure
    }
  }, [draftKey]);

  const migrateDraftScope = useCallback((nextWizard) => {
    const scopedKey = getOnboardingScopeKey();
    if (scopedKey !== draftKey) {
      try {
        localStorage.removeItem(draftKey);
      } catch (e) { }
      setDraftKey(scopedKey);
      persist(nextWizard || wizard, scopedKey);
    }
  }, [draftKey, persist, wizard]);

  const patch = (partial) => {
    setWizard((prev) => {
      const next = { ...prev, ...partial };
      persist(next);
      return next;
    });
  };

  const goTo = (index) => {
    const clamped = Math.max(0, Math.min(STEPS.length - 1, index));
    setCurrent(clamped);
    setWizard((prev) => {
      const next = { ...prev, __step: clamped };
      persist(next);
      return next;
    });
  };

  const onNext = () => goTo(current + 1);
  const onBack = () => goTo(current - 1);

  const goLive = () => {
    const blockers = getBlockingSetupItems(readiness, wizard);
    if (blockers.length) {
      message.warning(blockers[0]);
      return;
    }
    localStorage.removeItem(draftKey);
    navigate("/dashboard");
  };

  const refreshReadiness = useCallback(() => {
    setReadinessLoading(true);
    return loadOnboardingReadiness(wizard.company?.id)
      .then((nextReadiness) => {
        setReadiness(nextReadiness);
        if (nextReadiness.company.record?.id) {
          setWizard((prev) => {
            const next = {
              ...prev,
              company: {
                id: nextReadiness.company.record.id,
                name: nextReadiness.company.record.name,
                short_name: nextReadiness.company.record.short_name || nextReadiness.company.record.code,
              },
              org: { ...(prev.org || {}), counts: nextReadiness.org.counts },
              employees: nextReadiness.employees.count > 0
                ? { ...(prev.employees || {}), existing: true, count: nextReadiness.employees.count }
                : prev.employees,
            };
            persist(next);
            setTimeout(() => migrateDraftScope(next), 0);
            return next;
          });
        }
        return nextReadiness;
      })
      .catch(() => message.warning("Could not refresh setup data right now"))
      .finally(() => setReadinessLoading(false));
  }, [migrateDraftScope, persist, wizard.company?.id]);

  useEffect(() => {
    refreshReadiness().finally(() => setInitialReadinessLoading(false));
    // eslint-disable-next-line
  }, []);

  // Presets follow the industry/country chosen in step 1 (defaults to first
  // industry + Pakistan until the user picks).
  const presets = useMemo(
    () => resolvePresets(wizard.industry, wizard.country_code),
    [wizard.industry, wizard.country_code]
  );

  const statuses = useMemo(() => buildStepStatuses(readiness, wizard), [readiness, wizard]);
  const blockers = useMemo(() => getBlockingSetupItems(readiness, wizard), [readiness, wizard]);
  const skipStep = (key) => patch({ skipped: { ...(wizard.skipped || {}), [key]: true } });
  const clearSkip = (key) => {
    const nextSkipped = { ...(wizard.skipped || {}) };
    delete nextSkipped[key];
    patch({ skipped: nextSkipped });
  };

  const stepProps = {
    wizard,
    presets,
    readiness,
    readinessLoading,
    statuses,
    patch,
    refreshReadiness,
    skipStep,
    clearSkip,
    onNext,
    onBack,
    goLive,
  };

  const renderStep = () => {
    switch (current) {
      case 0: return <Step1Org { ...stepProps } />;
      case 1: return <Step3Employees { ...stepProps } />;
      case 2: return <StepShiftAssignment { ...stepProps } />;
      case 3: return <StepAttendanceMachines { ...stepProps } />;
      case 4: return <StepLeaveAssignments { ...stepProps } />;
      case 5: return <StepLoanSettings { ...stepProps } />;
      case 6: return <StepRequestFlow { ...stepProps } />;
      case 7: return <StepPayrollPolicies { ...stepProps } />;
      case 8: return <Step6GoLive { ...stepProps } />;
      default: return null;
    }
  };

  const pct = Math.round(((current + 1) / STEPS.length) * 100);

  return (
    <BodyComponent>
      <HeaderComponent
        headers={ {
          title: "Quick Setup",
          subtitle:
            "Review what is already configured and finish only the missing setup.",
        } }
      />
      { initialReadinessLoading ? (
        <div style={ { minHeight: 360, display: "flex", alignItems: "center", justifyContent: "center" } }>
          <Spin tip="Loading setup data..." />
        </div>
      ) : (
        <div className="onboarding-wrap">
          <div className="onb-readiness-banner">
            <div>
              <div className="onb-readiness-banner-title">Setup readiness</div>
              <div className="onb-readiness-banner-sub">
                { blockers.length
                  ? `${blockers.length} required item${blockers.length === 1 ? "" : "s"} need attention before setup can finish.`
                  : "Required setup is complete. Optional modules can be reviewed now or later." }
              </div>
            </div>
            <Spin spinning={ readinessLoading } size="small" />
          </div>
          <div className="onboarding-stepper">
            <div className="onb-steps-top">
              <div className="onb-steps-heading">
                Quick Setup
                <span className="sep">·</span>
                <span className="cur">{ STEPS[current]?.title }</span>
              </div>
              <div className="onb-steps-progress">
                <div className="onb-progress-track">
                  <div className="onb-progress-fill" style={ { width: `${pct}%` } } />
                </div>
                <span className="onb-progress-label">
                  Step { current + 1 } of { STEPS.length }
                </span>
              </div>
            </div>

            <div className="onb-steps">
              { STEPS.map((s, i) => {
                const status = visualStepStatus(i, current);
                const setupStatus = statuses[s.key];
                return (
                  <div
                    key={ s.key }
                    className={ `onb-step ${status}` }
                    onClick={ () => goTo(i) }
                    role="button"
                    tabIndex={ 0 }
                  >
                    <div className="onb-step-node">
                      { status === "done" ? <span className="onb-check">✓</span> : i + 1 }
                    </div>
                    <div className="onb-step-label">{ s.title }</div>
                    <StatusTag status={ setupStatus || SETUP_STATUS.optional } />
                  </div>
                );
              }) }
            </div>
          </div>

          <div className="onboarding-step-body">{ renderStep() }</div>
        </div>
      ) }
    </BodyComponent>
  );
};

export default OnboardingWizard;
